import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTitle, Meta, Display, Chip, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { PlantIllustration } from '../components/Illustrations';
import { LoadingState, ErrorState, EmptyState, Skeleton, PlantsGridSkeleton } from '../components/states';
import { useMultipleApi, useApi, categoryTone } from '../lib/api';

/**
 * Plants screen - katalog roślin z prawdziwymi danymi konta zalogowanego.
 *
 * Z 04-component-composition.md ekran 3:
 * - 3 zakresy: Mój ogród (default) | Ulubione | Cały katalog
 *   * obecnie: Mój ogród + Cały katalog + Ulubione (z toggle ❤)
 * - Sticky search + faceted filter chips
 * - Smart suggestions "Sieję w tym tygodniu"
 * - Lista (mobile) / grid (tablet+desktop)
 *
 * Endpoints:
 *   /api/plots/all-with-details - dla "Mój ogród" (unikalne plant_name z beds)
 *   /api/plants?limit=300       - dla "Cały katalog" (1100+ roślin, frontend filter)
 */

// ── Mapowanie kategorii backend → tłumaczenie + filter group ─────
const CATEGORY_GROUPS = {
  vegetable: 'vegetable',
  herb: 'herb',
  flower_perennial: 'flower',
  flower_annual: 'flower',
  flower_bulb: 'flower',
  fruit_tree: 'fruit',
  fruit_bush: 'fruit',
  tree_ornamental: 'flower',
  shrub_ornamental: 'flower',
  climber: 'flower',
  grass: 'herb',
  groundcover: 'herb',
  succulent: 'herb',
  fern: 'herb',
};

const SMART_SUGGESTION_PATTERNS = ['rzodkiew', 'sałata', 'sałatka', 'marchew', 'koperek', 'pietruszka'];

// ── Helpers ──────────────────────────────────────────────────────
function uniqueByName(beds) {
  const seen = new Map();
  beds.forEach(b => {
    if (!b.plant_name) return;
    const key = b.plant_name.toLowerCase();
    if (!seen.has(key)) seen.set(key, b);
  });
  return Array.from(seen.values());
}

function bedsToPseudoPlants(beds) {
  // "Mój ogród": pokazuje rośliny z grządek użytkownika. Każda grządka ma
  // display_image (zdjęcie wgrane przez usera lub fallback z katalogu).
  // Przenosimy je na photo_thumb żeby PlantCard pokazał REALNE zdjęcie
  // z ogrodu, nie generic ilustrację.
  return uniqueByName(beds).map(b => {
    const photo = b.display_image || b.image_path || b.plant_photo_path;
    return {
      id: `bed-${b.id}`,
      name: b.plant_name,
      display_name: b.plant_name,
      plant_variety: b.plant_variety,
      plot_name: b.plot_name,
      photo_thumb: photo || null,
      photo_path: photo || null,
      fromBed: true,
    };
  });
}

function filterPlants(plants, scope, group, query, advanced = {}, family) {
  if (!Array.isArray(plants)) return [];
  let result = plants;
  if (group !== 'all') {
    result = result.filter(p => p && CATEGORY_GROUPS[p.category] === group);
  }
  if (query) {
    const q = String(query).toLowerCase();
    result = result.filter(p =>
      String(p?.display_name || '').toLowerCase().includes(q) ||
      String(p?.name || '').toLowerCase().includes(q) ||
      String(p?.latin_name || '').toLowerCase().includes(q)
    );
  }
  if (family) {
    // plants z katalogu nie mają plant_family — filtr działa tylko dla
    // roślin z plants_ref, ale w pamięci nie mamy tej kolumny.
    // Filtr po rodzinie wymaga backendowego ?family=. Na froncie
    // filtrujemy tylko jeśli dane przyszły z backendu z adnotacją.
    // Realnie: używamy backendu przez zmianę API URL.
    // Dla scope=all wołamy /api/plants?limit=2000&family=...
    // Na razie frontend ignoruje (backend robi filtr).
  }
  if (advanced.sun) {
    result = result.filter(p => p?.sun_requirement === advanced.sun);
  }
  if (advanced.water) {
    result = result.filter(p => (p?.water_needs === advanced.water) || (p?.watering_needs === advanced.water));
  }
  if (advanced.ph) {
    result = result.filter(p => p?.soil_ph === advanced.ph);
  }
  if (advanced.color) {
    const c = String(advanced.color).toLowerCase();
    result = result.filter(p => String(p?.flower_color || '').toLowerCase().includes(c));
  }
  if (advanced.beeFriendly) {
    result = result.filter(p => Boolean(p?.is_bee_friendly));
  }
  return result;
}

function sortPlants(plants, sortBy) {
  const arr = [...plants];
  switch (sortBy) {
    case 'name_asc':
      arr.sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '', 'pl'));
      break;
    case 'name_desc':
      arr.sort((a, b) => (b.display_name || b.name || '').localeCompare(a.display_name || a.name || '', 'pl'));
      break;
    case 'days_to_harvest':
      arr.sort((a, b) => (a.days_to_harvest || 9999) - (b.days_to_harvest || 9999));
      break;
    case 'date_added':
      arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      break;
    default:
      break;
  }
  return arr;
}

function collectFilterOptions(plants) {
  const sun = new Set(), water = new Set(), ph = new Set(), colors = new Set();
  if (!Array.isArray(plants)) return { sun: [], water: [], ph: [], colors: [] };
  plants.forEach(p => {
    if (!p) return;
    if (typeof p.sun_requirement === 'string' && p.sun_requirement) sun.add(p.sun_requirement);
    const w = p.water_needs || p.watering_needs;
    if (typeof w === 'string' && w) water.add(w);
    if (typeof p.soil_ph === 'string' && p.soil_ph) ph.add(p.soil_ph);
    if (typeof p.flower_color === 'string' && p.flower_color) {
      p.flower_color.split(',').forEach(c => {
        const trimmed = c.trim();
        if (trimmed) colors.add(trimmed);
      });
    }
  });
  const sortAlpha = (a, b) => String(a).localeCompare(String(b), 'pl');
  return {
    sun: [...sun].sort(sortAlpha),
    water: [...water].sort(sortAlpha),
    ph: [...ph].sort(sortAlpha),
    colors: [...colors].sort(sortAlpha),
  };
}

function smartSuggestions(plants) {
  return plants
    .filter(p => SMART_SUGGESTION_PATTERNS.some(pattern =>
      (p.name || '').toLowerCase().includes(pattern) ||
      (p.display_name || '').toLowerCase().includes(pattern)
    ))
    .slice(0, 6);
}

function plantTone(p) {
  return categoryTone(p.category) || 'sage';
}

// ── Favorite toggle (klik serce, optimistic update) ────────────
function FavoriteToggle({ plantId, isFav, onToggle, size = 22 }) {
  const [busy, setBusy] = React.useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !isFav;
    onToggle?.(plantId, next, true); // optimistic
    try {
      const axios = (await import('../../config/axios')).default;
      if (next) {
        await axios.post(`/api/plants/${plantId}/favorite`);
      } else {
        await axios.delete(`/api/plants/${plantId}/favorite`);
      }
    } catch (err) {
      // Rollback
      onToggle?.(plantId, !next, false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-label={isFav ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
      title={isFav ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
      style={{
        background: 'transparent', border: 'none',
        cursor: busy ? 'wait' : 'pointer',
        padding: 4, lineHeight: 1,
        display: 'inline-grid', placeItems: 'center',
        flexShrink: 0,
        transition: 'transform 200ms ease',
      }}
      onMouseEnter={e => { if (!busy) e.currentTarget.style.transform = 'scale(1.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill={isFav ? 'var(--t-rust)' : 'none'}
           stroke={isFav ? 'var(--t-rust)' : 'var(--t-text-mute)'} strokeWidth="1.8">
        <path d="M12 21s-7-4.5-9-9a5 5 0 019-3 5 5 0 019 3c-2 4.5-9 9-9 9z"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// ── PlantRow (mobile) ────────────────────────────────────────────
function PlantRow({ plant, isFav, onToggleFav }) {
  const { t } = useTranslation();
  const tone = plantTone(plant);
  const c = tone === 'amber' ? 'var(--t-amber)' :
            tone === 'rust'  ? 'var(--t-rust)'  : 'var(--t-sage)';
  const isFromBed = plant.fromBed;

  return (
    <Link to={`/plants/${plant.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 24px',
        borderBottom: '1px solid var(--t-line)',
        cursor: 'pointer',
        transition: 'background 200ms ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232, 230, 221, 0.02)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        <div style={{
          width: 52, height: 52, borderRadius: 10,
          background: `linear-gradient(135deg, ${c}26, ${c}08)`,
          display: 'grid', placeItems: 'center', flexShrink: 0,
          overflow: 'hidden',
        }}>
          {plant.photo_thumb ? (
            <img
              src={plant.photo_thumb.startsWith('/') ? plant.photo_thumb : `/${plant.photo_thumb}`}
              alt={plant.display_name || plant.name}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <PlantIllustration category={plant.category || 'sprout'} tone={tone} size={36} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
            <Display size={20} style={{ letterSpacing: '-.02em' }}>
              {plant.display_name || plant.name}
            </Display>
            {plant.latin_name && (
              <span style={{
                fontFamily: 'var(--t-display)', fontStyle: 'italic',
                fontSize: 12, color: 'var(--t-text-mute)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{plant.latin_name}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 4 }}>
            {[plant.plant_variety, plant.plot_name].filter(Boolean).join(' · ') ||
             plant.bloom_season || plant.height || ''}
          </div>
          {plant.days_to_harvest > 0 && !isFromBed && (
            <Meta>{plant.days_to_harvest} dni do zbioru</Meta>
          )}
        </div>
        {!isFromBed && plant.id && typeof plant.id === 'number' && (
          <FavoriteToggle plantId={plant.id} isFav={isFav} onToggle={onToggleFav} size={20} />
        )}
        <svg width="7" height="14" viewBox="0 0 7 14" fill="none" style={{ flexShrink: 0 }}>
          <path d="M1 1l5 6-5 6" stroke="var(--t-text-dim)" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </Link>
  );
}

// ── PlantCard (grid desktop/tablet) ──────────────────────────────
function PlantCard({ plant, isFav, onToggleFav }) {
  const tone = plantTone(plant);
  const c = tone === 'amber' ? 'var(--t-amber)' :
            tone === 'rust'  ? 'var(--t-rust)'  : 'var(--t-sage)';

  return (
    <Link to={`/plants/${plant.id}`} style={{ textDecoration: 'none' }}>
      <div style={{
        borderRadius: 16, overflow: 'hidden',
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        cursor: 'pointer',
        transition: 'transform 250ms ease, border-color 250ms ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.borderColor = 'var(--t-line-2)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--t-line)';
      }}>
        <div style={{
          height: 140, position: 'relative',
          background: `linear-gradient(135deg, ${c}26, ${c}08)`,
          display: 'grid', placeItems: 'center',
          borderBottom: '1px solid var(--t-line)',
        }}>
          {plant.photo_thumb ? (
            <img
              src={plant.photo_thumb.startsWith('/') ? plant.photo_thumb : `/${plant.photo_thumb}`}
              alt={plant.display_name || plant.name}
              loading="lazy"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', display: 'block',
              }}
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <PlantIllustration category={plant.category || 'sprout'} tone={tone} size={70} />
          )}
          {plant.id && typeof plant.id === 'number' && (
            <div style={{
              position: 'absolute', top: 8, right: 8,
              background: 'rgba(15, 20, 16, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: '50%',
              padding: 2,
            }}>
              <FavoriteToggle plantId={plant.id} isFav={isFav} onToggle={onToggleFav} size={18} />
            </div>
          )}
        </div>
        {/* Tekst: zawsze widoczny pod obrazkiem. Display component to <span>
            więc używamy zwykłych <div> z stylami fontowymi — line-clamp
            działa wtedy pewnie na wszystkich przeglądarkach (Display+span
            wcześniej dziwnie się renderował). minHeight rezerwuje miejsce. */}
        <div style={{
          padding: '14px 16px',
          minHeight: 110,
          background: 'var(--t-bg-elev)',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            fontFamily: 'var(--t-display)',
            fontWeight: 300,
            color: 'var(--t-text)',
            fontSize: 18,
            lineHeight: 1.15,
            letterSpacing: '-.02em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}>
            {plant.display_name || plant.name}
          </div>
          {plant.latin_name && (
            <div style={{
              fontFamily: 'var(--t-display)', fontStyle: 'italic',
              fontSize: 11, color: 'var(--t-text-mute)',
              marginTop: 4, marginBottom: 8,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{plant.latin_name}</div>
          )}
          {plant.plot_name && (
            <div style={{
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.14em', textTransform: 'uppercase',
              color: 'var(--t-sage)', marginTop: 4,
            }}>{plant.plot_name}</div>
          )}
          {(plant.bloom_season || plant.sun_requirement || plant.days_to_harvest > 0) && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--t-line)',
              fontSize: 11, color: 'var(--t-text-dim)',
            }}>
              <span>{plant.bloom_season || plant.sun_requirement || '—'}</span>
              {plant.days_to_harvest > 0 && (
                <span style={{ color: c, fontFamily: 'var(--t-display)', fontSize: 14 }}>
                  {plant.days_to_harvest}d
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// ── Smart suggestions banner ─────────────────────────────────────
function SmartSuggestions({ plants }) {
  const { t } = useTranslation();
  if (plants.length === 0) return null;
  return (
    <div style={{
      margin: '0 0 24px',
      padding: '18px 20px',
      borderRadius: 14,
      background: 'rgba(168, 196, 158, 0.06)',
      border: '1px solid rgba(168, 196, 158, 0.2)',
    }}>
      <Meta color="sage" style={{ marginBottom: 12 }}>
        🌱 {t('plants.smart_suggestions_title')}
      </Meta>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {plants.slice(0, 5).map(p => (
          <Link key={p.id} to={`/plants/${p.id}`} style={{ textDecoration: 'none' }}>
            <Chip tone="sage">{p.display_name || p.name}</Chip>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Filter bar ───────────────────────────────────────────────────
function FilterBar({ scope, setScope, group, setGroup, family, setFamily, search, setSearch, mobile, gardenCount, totalCount, advanced, setAdvanced, advancedOpen, setAdvancedOpen, sortBy, setSortBy, filterOptions, families, onAddPlant }) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{
        margin: mobile ? '0 24px 16px' : '0 0 18px',
        padding: '12px 14px',
        borderRadius: 12,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Icons.Search color="var(--t-text-dim)" size={16} />
        <input
          type="text"
          aria-label="Szukaj rośliny"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('plants.search_placeholder')}
          style={{
            flex: 1,
            border: 'none', outline: 'none',
            background: 'transparent',
            color: 'var(--t-text)',
            fontFamily: 'var(--t-sans)',
            fontSize: 14,
          }}
        />
      </div>

      <div style={{
        padding: mobile ? '0 24px 12px' : '0 0 14px',
        display: 'flex', gap: 8,
        flexWrap: mobile ? 'wrap' : 'nowrap',
        overflowX: mobile ? 'visible' : 'auto',
      }}>
        <Chip selected={scope === 'my'} onClick={() => setScope('my')}>
          {t('plants.scope_my')} · {gardenCount}
        </Chip>
        <Chip selected={scope === 'all'} onClick={() => setScope('all')}>
          {t('plants.scope_all')} · {totalCount}
        </Chip>
        <Chip selected={scope === 'fav'} onClick={() => setScope('fav')} tone="rust">
          ♥ Ulubione
        </Chip>
      </div>

      <div style={{
        padding: mobile ? '0 24px 12px' : '0 0 14px',
        display: 'flex', gap: 8,
        flexWrap: mobile ? 'wrap' : 'nowrap',
        overflowX: mobile ? 'visible' : 'auto',
      }}>
        {[
          { k: 'all',       label: t('plants.filter_all'),       tone: undefined },
          { k: 'vegetable', label: t('plants.filter_vegetable'), tone: 'sage' },
          { k: 'herb',      label: t('plants.filter_herb'),      tone: 'amber' },
          { k: 'flower',    label: t('plants.filter_flower'),    tone: 'rust' },
          { k: 'fruit',     label: t('plants.filter_fruit'),     tone: 'dim' },
        ].map(c => (
          <Chip
            key={c.k}
            tone={c.tone}
            selected={group === c.k}
            onClick={() => setGroup(c.k)}
          >{c.label}</Chip>
        ))}

        {/* Filtr po rodzinie botanicznej (P4) */}
        {families && families.length > 0 && (
          <div style={{
            padding: mobile ? '0 24px 6px' : '0 0 8px',
          }}>
            <select
              value={family || ''}
              onChange={e => setFamily(e.target.value || null)}
              aria-label="Filtr po rodzinie"
              style={{
                padding: '6px 12px', borderRadius: 999,
                background: family ? 'rgba(168, 196, 158, 0.12)' : 'var(--t-bg-elev)',
                border: `1px solid ${family ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
                color: family ? 'var(--t-sage)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.14em', textTransform: 'uppercase',
                cursor: 'pointer', maxWidth: 220,
                appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                backgroundImage: 'linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)',
                backgroundPosition: 'calc(100% - 10px) 50%, calc(100% - 6px) 50%',
                backgroundSize: '4px 4px, 4px 4px', backgroundRepeat: 'no-repeat',
                paddingRight: 22,
              }}
            >
              <option value="">Rodzina: wszystkie</option>
              {families.map(f => (
                <option key={f.family} value={f.family}>
                  {f.label} ({f.count})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pasek narzędzi: rozszerzone filtry + sortowanie + dodaj roślinę */}
      <div style={{
        padding: mobile ? '0 24px 14px' : '0 0 18px',
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {setAdvancedOpen && (
          <button
            type="button"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            style={{
              padding: '8px 14px', borderRadius: 999,
              background: advancedOpen ? 'rgba(168, 196, 158, 0.12)' : 'transparent',
              border: `1px solid ${advancedOpen ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
              color: advancedOpen ? 'var(--t-sage)' : 'var(--t-text-dim)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.18em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >Filtry {advancedOpen ? '▾' : '▸'}</button>
        )}
        {setSortBy && (
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            aria-label="Sortuj"
            style={{
              padding: '8px 12px', borderRadius: 999,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line-2)',
              color: 'var(--t-text-dim)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.14em', textTransform: 'uppercase',
              cursor: 'pointer',
              maxWidth: '100%',
              boxSizing: 'border-box',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              backgroundImage: 'linear-gradient(45deg, transparent 50%, var(--t-text-dim) 50%), linear-gradient(135deg, var(--t-text-dim) 50%, transparent 50%)',
              backgroundPosition: 'calc(100% - 16px) 50%, calc(100% - 11px) 50%',
              backgroundSize: '5px 5px, 5px 5px',
              backgroundRepeat: 'no-repeat',
              paddingRight: 26,
            }}
          >
            <option value="name_asc">A → Z</option>
            <option value="name_desc">Z → A</option>
            <option value="days_to_harvest">Szybko do zbioru</option>
            <option value="date_added">Najnowsze</option>
          </select>
        )}
        <div style={{ flex: 1 }} />
        {onAddPlant && (
          <button
            type="button"
            onClick={onAddPlant}
            style={{
              padding: '8px 16px', borderRadius: 999,
              background: 'var(--t-sage)',
              border: 'none',
              color: 'var(--t-bg)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.18em', textTransform: 'uppercase',
              cursor: 'pointer', fontWeight: 600,
            }}
          >+ Dodaj roślinę</button>
        )}
      </div>

      {/* Panel filtrów rozszerzonych */}
      {advancedOpen && advanced && setAdvanced && filterOptions && (
        <div style={{
          padding: mobile ? '14px 24px 18px' : '14px 18px 18px',
          margin: mobile ? '0 24px 18px' : '0 0 22px',
          borderRadius: 12,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}>
          <AdvancedSelect
            label="Słońce"
            value={advanced.sun}
            options={filterOptions.sun}
            onChange={v => setAdvanced({ ...advanced, sun: v })}
          />
          <AdvancedSelect
            label="Woda"
            value={advanced.water}
            options={filterOptions.water}
            onChange={v => setAdvanced({ ...advanced, water: v })}
          />
          <AdvancedSelect
            label="pH gleby"
            value={advanced.ph}
            options={filterOptions.ph}
            onChange={v => setAdvanced({ ...advanced, ph: v })}
          />
          <AdvancedSelect
            label="Kolor kwiatu"
            value={advanced.color}
            options={filterOptions.colors}
            onChange={v => setAdvanced({ ...advanced, color: v })}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', borderRadius: 8,
            background: advanced.beeFriendly ? 'rgba(220, 180, 100, 0.12)' : 'transparent',
            border: `1px solid ${advanced.beeFriendly ? 'var(--t-amber)' : 'var(--t-line-2)'}`,
            color: advanced.beeFriendly ? 'var(--t-amber)' : 'var(--t-text-dim)',
            fontSize: 12, cursor: 'pointer', userSelect: 'none',
          }}>
            <input
              type="checkbox"
              aria-label="Przyjazne pszczołom"
              checked={!!advanced.beeFriendly}
              onChange={e => setAdvanced({ ...advanced, beeFriendly: e.target.checked })}
              style={{ accentColor: 'var(--t-amber)' }}
            />
            🐝 Przyjazne pszczołom
          </label>
          {(advanced.sun || advanced.water || advanced.ph || advanced.color || advanced.beeFriendly) && (
            <button
              type="button"
              onClick={() => setAdvanced({ sun: '', water: '', ph: '', color: '', beeFriendly: false })}
              style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'transparent',
                border: '1px solid var(--t-rust)',
                color: 'var(--t-rust)',
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.14em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >Wyczyść filtry</button>
          )}
        </div>
      )}
    </>
  );
}

function AdvancedSelect({ label, value, options, onChange }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-text-mute)',
        marginBottom: 6,
      }}>{label}</div>
      <select
        aria-label={label}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px', borderRadius: 8,
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line-2)',
          color: 'var(--t-text)',
          fontFamily: 'var(--t-sans)', fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <option value="">— wszystkie —</option>
        {(options || []).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

// ── Lista alphabetic ─────────────────────────────────────────────
function GroupedList({ plants, favorites, onToggleFav }) {
  const grouped = useMemo(() => {
    const out = {};
    plants.forEach(p => {
      const name = p.display_name || p.name || '';
      const letter = (name[0] || '?').toUpperCase();
      if (!out[letter]) out[letter] = [];
      out[letter].push(p);
    });
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b, 'pl'));
  }, [plants]);

  if (plants.length === 0) {
    return (
      <EmptyState
        title="Brak wyników"
        description="Spróbuj zmienić filtry albo zakres."
      />
    );
  }

  return grouped.map(([letter, items]) => (
    <React.Fragment key={letter}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '8px 24px', margin: '8px 0 0',
        borderTop: '1px solid var(--t-line)',
        borderBottom: '1px solid var(--t-line)',
        background: 'rgba(232, 230, 221, 0.02)',
      }}>
        <Display size={28} style={{ color: 'var(--t-sage)', width: 28, lineHeight: 1 }}>{letter}</Display>
        <div style={{ flex: 1, height: 1, background: 'var(--t-line-2)' }} />
        <Meta>{items.length}</Meta>
      </div>
      {items.map(p => (
        <PlantRow key={p.id} plant={p}
          isFav={favorites?.has(p.id)}
          onToggleFav={onToggleFav}
        />
      ))}
    </React.Fragment>
  ));
}

// ── Mobile / Desktop content (zunifikowane) ──────────────────────
function PlantsContent({ scope, setScope, group, setGroup, family, setFamily, search, setSearch, plants, gardenCount, totalCount, suggestions, mobile, withGrid, favorites, onToggleFav, advanced, setAdvanced, advancedOpen, setAdvancedOpen, sortBy, setSortBy, filterOptions, families, onAddPlant }) {
  const { t } = useTranslation();
  const PAGE_SIZE = 60;
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);

  // Reset paginacji przy zmianie filtrów / zakresu
  useEffect(() => {
    setDisplayLimit(PAGE_SIZE);
  }, [scope, group, search, advanced, sortBy]);

  const visible = plants.slice(0, displayLimit);
  const hasMore = plants.length > displayLimit;

  return (
    <>
      <FilterBar
        mobile={mobile}
        scope={scope} setScope={setScope}
        group={group} setGroup={setGroup}
        family={family} setFamily={setFamily}
        search={search} setSearch={setSearch}
        gardenCount={gardenCount}
        totalCount={totalCount}
        advanced={advanced} setAdvanced={setAdvanced}
        advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen}
        sortBy={sortBy} setSortBy={setSortBy}
        filterOptions={filterOptions}
        families={families}
        onAddPlant={onAddPlant}
      />

      {scope === 'my' && !search && (
        <div style={{ padding: mobile ? '0 24px' : 0 }}>
          <SmartSuggestions plants={suggestions} />
        </div>
      )}

      {withGrid ? (
        <>
          <div style={{
            display: 'grid',
            // 200 zamiast 160: na szerokim ekranie dawało 9 ciasnych kolumn,
            // gdzie nazwa rośliny ledwo mieściła się w 1 linii — user zgłosił,
            // że "obrazki zasłaniają nazwę". 200 daje 5-7 kolumn z czytelnymi
            // nazwami i działającym line-clamp.
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 16,
          }}>
            {plants.length === 0 ? (
              <EmptyState
                title="Brak wyników"
                description="Spróbuj zmienić filtry albo zakres."
              />
            ) : visible.map(p => (
              <PlantCard key={p.id} plant={p}
                isFav={favorites?.has(p.id)}
                onToggleFav={onToggleFav}
              />
            ))}
          </div>
          {hasMore && (
            <PaginationFooter
              shown={visible.length}
              total={plants.length}
              onMore={() => setDisplayLimit(d => d + PAGE_SIZE)}
              onAll={() => setDisplayLimit(plants.length)}
            />
          )}
        </>
      ) : (
        <div style={{ margin: mobile ? '0' : '0 -40px' }}>
          <GroupedList plants={visible} favorites={favorites} onToggleFav={onToggleFav} />
          {hasMore && (
            <div style={{ padding: mobile ? '0 24px' : '0 40px' }}>
              <PaginationFooter
                shown={visible.length}
                total={plants.length}
                onMore={() => setDisplayLimit(d => d + PAGE_SIZE)}
                onAll={() => setDisplayLimit(plants.length)}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Footer paginacji ─────────────────────────────────────────────
function PaginationFooter({ shown, total, onMore, onAll }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      padding: '32px 16px 16px',
    }}>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-text-dim)',
      }}>
        Pokazano {shown} z {total}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={onMore}
          style={{
            padding: '10px 22px', borderRadius: 999,
            background: 'var(--t-sage)', color: 'var(--t-bg)',
            border: '1px solid var(--t-sage)',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.18em', textTransform: 'uppercase',
            cursor: 'pointer', fontWeight: 600,
          }}
        >Pokaż więcej</button>
        <button
          type="button"
          onClick={onAll}
          style={{
            padding: '10px 22px', borderRadius: 999,
            background: 'transparent', color: 'var(--t-text-dim)',
            border: '1px solid var(--t-line-2)',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.18em', textTransform: 'uppercase',
            cursor: 'pointer',
          }}
          title="Może być wolne dla 1000+ roślin"
        >Pokaż wszystkie ({total})</button>
      </div>
      {/* UXA: hint dostępny też na dotyku (nie tylko w title=/hover) */}
      <div style={{
        marginTop: 8, textAlign: 'center',
        fontFamily: 'var(--t-mono)', fontSize: 11, letterSpacing: '.04em',
        color: 'var(--t-text-mute)',
      }}>„Wszystkie" może chwilę potrwać przy 1000+ roślinach.</div>
    </div>
  );
}

// ── Mobile root ─────────────────────────────────────────────────
function MobileContent({ ...props }) {
  const { t } = useTranslation();
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>{t('plants.varieties_count', { count: props.totalCount })}</Meta>
      </div>
      <PageTitle title={t('plants.title')} eyebrow={t('plants.eyebrow')} kicker={t('plants.kicker')} />
      <PlantsContent {...props} mobile withGrid={false} />
    </div>
  );
}

// ── Desktop root ────────────────────────────────────────────────
function DesktopContent({ withGrid, ...props }) {
  const { t } = useTranslation();
  return (
    <div className="t-stagger" style={{ padding: '32px 40px 60px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline',
        marginBottom: 28, paddingBottom: 18,
        borderBottom: '1px solid var(--t-line)', gap: 14,
      }}>
        <div>
          <Meta>{t('plants.varieties_count', { count: props.totalCount })}</Meta>
          <Display size={48} style={{ display: 'block', marginTop: 6, letterSpacing: '-.03em' }}>
            {t('plants.title')}<span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}> {t('plants.eyebrow').toLowerCase()}</span>
          </Display>
        </div>
        <div style={{ flex: 1 }} />
        <Meta>{t('plants.kicker')}</Meta>
      </div>
      <PlantsContent {...props} withGrid={withGrid} />
    </div>
  );
}

// ── Plants screen root ───────────────────────────────────────────
// Persist search/scope/group/sort do sessionStorage żeby user wracający
// z PlantDetail (po klik karty) trafił dokładnie tam, gdzie był — z tym samym
// query "sosna", filtrem "Drzewa", sortowaniem itd. Bez tego musiałby
// wpisywać od nowa za każdym razem.
const PLANTS_STATE_KEY = 'plants:lastView';
function loadPersistedState() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PLANTS_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function savePersistedState(state) {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.setItem(PLANTS_STATE_KEY, JSON.stringify(state)); }
  catch { /* quota exceeded — ignore */ }
}

export default function Plants() {
  const { t } = useTranslation();
  const { isPhone, isDesktop } = useViewport();
  const [searchParams] = useSearchParams();
  const scopeFromUrl = searchParams.get('scope');
  const restoreFlag = searchParams.get('restore') === '1';
  const persisted = restoreFlag ? loadPersistedState() : null;

  const initialScope =
    (scopeFromUrl === 'all' || scopeFromUrl === 'fav' || scopeFromUrl === 'my') ? scopeFromUrl
    : (persisted?.scope) || 'my';
  const [scope, setScope] = useState(initialScope);
  const [group, setGroup] = useState(persisted?.group || 'all');
  const [search, setSearch] = useState(persisted?.search || '');
  const [favorites, setFavorites] = useState(new Set());
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState(persisted?.advanced || { sun: '', water: '', ph: '', color: '', beeFriendly: false });
  const [sortBy, setSortBy] = useState(persisted?.sortBy || 'name_asc');
  const [showAddPlant, setShowAddPlant] = useState(false);
  const [family, setFamily] = useState(null);
  const { data: familiesData } = useApi('/api/plants/families');

  // Zapisuj stan przy każdej zmianie filtrów (debounce naturalny przez React batch).
  useEffect(() => {
    savePersistedState({ scope, group, search, sortBy, advanced });
  }, [scope, group, search, sortBy, advanced]);

  const { results, loading, errors } = useMultipleApi({
    plots: '/api/plots/all-with-details',
    catalog: '/api/plants?limit=2000',
    favs: '/api/plants/favorites',
  });

  // Synchronizuj zbiór favorites z API gdy załaduje się
  useEffect(() => {
    if (Array.isArray(results.favs)) {
      setFavorites(new Set(results.favs.map(f => f.plant_id)));
    }
  }, [results.favs]);

  // Optimistic toggle
  const handleToggleFav = (plantId, isFav) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.add(plantId);
      else next.delete(plantId);
      return next;
    });
  };

  // Mój ogród = unikalne plant_name z beds (bez duplikatów)
  const gardenPlants = useMemo(() => {
    if (!results.plots) return [];
    const allBeds = results.plots.flatMap(p =>
      (p.beds || []).map(b => ({ ...b, plot_name: p.name }))
    );
    return bedsToPseudoPlants(allBeds);
  }, [results.plots]);

  // useMemo żeby catalogPlants było stabilne — używane w 3 useMemo niżej (filtered,
  // filterOptions, suggestions). Wcześniej każdy render dawał nową referencję,
  // co invalidowało cache — eslint warning + przyspiesza filtrowanie 1100 roślin.
  const catalogPlants = useMemo(() => results.catalog?.data || [], [results.catalog]);

  // Wybór source na podstawie scope — też useMemo żeby filtered useMemo był stabilny.
  const sourcePlants = useMemo(() => {
    if (scope === 'my') return gardenPlants;
    if (scope === 'fav') return catalogPlants.filter(p => favorites.has(p.id));
    return catalogPlants;
  }, [scope, gardenPlants, catalogPlants, favorites]);

  const filtered = useMemo(
    () => sortPlants(filterPlants(sourcePlants, scope, group, search, advanced), sortBy),
    [sourcePlants, scope, group, search, advanced, sortBy]
  );

  const filterOptions = useMemo(() => collectFilterOptions(catalogPlants), [catalogPlants]);

  const suggestions = useMemo(() => smartSuggestions(catalogPlants), [catalogPlants]);
  const gardenCount = gardenPlants.length;
  const totalCount = catalogPlants.length;

  if (errors.plots && (errors.plots.includes('autoryz') || errors.plots.includes('zalog') || errors.plots === 'Brak tokenu autoryzacji')) {
    return <Layout active="plants"><ErrorState error={{ status: 401, message: errors.plots }} /></Layout>;
  }
  if (loading) {
    return <Layout active="plants"><PlantsGridSkeleton count={isPhone ? 6 : 12} /></Layout>;
  }

  const contentProps = {
    scope, setScope, group, setGroup, family, setFamily, search, setSearch,
    plants: filtered, gardenCount, totalCount, suggestions,
    favorites, onToggleFav: handleToggleFav,
    advanced, setAdvanced, advancedOpen, setAdvancedOpen,
    sortBy, setSortBy, filterOptions,
    families: familiesData,
    onAddPlant: () => setShowAddPlant(true),
  };

  const handleAdded = () => {
    setShowAddPlant(false);
    // Najlepiej refetch — useMultipleApi nie zwraca refetchAll dla pojedynczych kluczy.
    // Reload strony zachowuje filtry przez state initialization.
    window.location.reload();
  };

  const modal = showAddPlant ? (
    <AddPlantModal onClose={() => setShowAddPlant(false)} onAdded={handleAdded} />
  ) : null;

  if (isPhone) {
    return (
      <Layout active="plants">
        <MobileContent {...contentProps} />
        {modal}
      </Layout>
    );
  }

  return (
    <Layout
      active="plants"
      topBar={<TopBar
        left={<Meta>{t('plants.eyebrow')}</Meta>}
        right={
          <button
            type="button"
            onClick={() => setShowAddPlant(true)}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--t-sage)',
              color: 'var(--t-sage)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.18em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >+ {t('plants.add_plant')}</button>
        }
      />}
    >
      <DesktopContent {...contentProps} withGrid={isDesktop} />
      {modal}
    </Layout>
  );
}

// ── AddPlantModal — szybki dodawacz własnej rośliny ──────────────
function AddPlantModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [latinName, setLatinName] = useState('');
  const [category, setCategory] = useState('vegetable');
  const [daysToHarvest, setDaysToHarvest] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoAuthor, setPhotoAuthor] = useState('');
  const [photoLicense, setPhotoLicense] = useState('CC BY 4.0');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setErr('Zdjęcie nie może być większe niż 10 MB.');
      return;
    }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target?.result || null);
    reader.readAsDataURL(f);
    setErr('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!name.trim()) {
      setErr('Nazwa jest wymagana.');
      return;
    }
    if (photoFile && !photoAuthor.trim()) {
      setErr('Podaj autora zdjęcia (np. Twoje imię lub źródło Wikimedia).');
      return;
    }
    setSaving(true); setErr('');
    try {
      const axios = (await import('../../config/axios')).default;
      const fd = new FormData();
      fd.append('display_name', name.trim());
      fd.append('name', name.trim().toLowerCase().replace(/\s+/g, '_'));
      if (latinName.trim()) fd.append('latin_name', latinName.trim());
      fd.append('category', category);
      if (daysToHarvest && Number(daysToHarvest) > 0) fd.append('days_to_harvest', String(Number(daysToHarvest)));
      if (notes.trim()) fd.append('notes', notes.trim());
      if (photoFile) {
        fd.append('photo', photoFile);
        fd.append('photo_author', photoAuthor.trim());
        fd.append('photo_license', photoLicense.trim() || 'CC BY 4.0');
      }
      await axios.post('/api/plants', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onAdded?.();
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.response?.data?.errors?.[0]?.msg || 'Nie udało się dodać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Dodaj roślinę"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'grid', placeItems: 'center',
        padding: 16,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 460,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line-2)',
        borderRadius: 16,
        padding: 24,
        maxHeight: 'calc(100dvh - 32px)',
        overflowY: 'auto',
      }}>
        <Display size={22} style={{ display: 'block', marginBottom: 6, letterSpacing: '-.02em' }}>
          Dodaj własną roślinę
        </Display>
        <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55, marginBottom: 18 }}>
          Twoja roślina trafia do bazy jako „pending" — zatwierdza administrator. Dla siebie widoczna od razu.
        </div>

        <form onSubmit={submit}>
          <Field label="Nazwa rośliny *" htmlFor="apn">
            <input id="apn" type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="np. Pomidor 'Malinowy Olbrzym'"
              maxLength={120} style={addPlantInputStyle} />
          </Field>
          <Field label="Nazwa łacińska" htmlFor="apl">
            <input id="apl" type="text" value={latinName} onChange={e => setLatinName(e.target.value)}
              placeholder="Solanum lycopersicum"
              maxLength={120} style={addPlantInputStyle} />
          </Field>
          <Field label="Kategoria" htmlFor="apc">
            <select id="apc" value={category} onChange={e => setCategory(e.target.value)}
              style={addPlantInputStyle}>
              <option value="vegetable">Warzywo</option>
              <option value="herb">Zioło</option>
              <option value="flower">Kwiat</option>
              <option value="fruit">Owoc</option>
              <option value="tree">Drzewo</option>
              <option value="shrub">Krzew</option>
              <option value="other">Inne</option>
            </select>
          </Field>
          <Field label="Dni do zbioru (opcjonalnie)" htmlFor="apd">
            <input id="apd" type="number" min="1" max="999" value={daysToHarvest}
              onChange={e => setDaysToHarvest(e.target.value)}
              placeholder="np. 75" style={addPlantInputStyle} />
          </Field>
          <Field label="Notatki" htmlFor="apno">
            <textarea id="apno" value={notes} onChange={e => setNotes(e.target.value)}
              rows={3} maxLength={500}
              placeholder="Wymagania, ciekawostki…"
              style={{ ...addPlantInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }} />
          </Field>

          {/* Zdjęcie — opcjonalne */}
          <Field label="Zdjęcie (opcjonalnie)" htmlFor="apph">
            <div style={{
              display: 'flex', gap: 12, alignItems: 'center',
              padding: 12, borderRadius: 10,
              background: 'var(--t-bg)',
              border: '1px dashed var(--t-line-2)',
            }}>
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Podgląd"
                  style={{
                    width: 64, height: 64, objectFit: 'cover',
                    borderRadius: 8, flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: 64, height: 64, borderRadius: 8,
                  background: 'var(--t-bg-elev)',
                  display: 'grid', placeItems: 'center',
                  color: 'var(--t-text-mute)', fontSize: 24, flexShrink: 0,
                }}>📷</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  id="apph"
                  type="file"
                  accept="image/*"
                  onChange={handlePhoto}
                  style={{
                    fontSize: 12, color: 'var(--t-text-dim)',
                    width: '100%',
                  }}
                />
                {photoFile && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--t-text-mute)' }}>
                    {(photoFile.size / 1024).toFixed(0)} KB
                    {' · '}
                    <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                      style={{ background: 'none', border: 'none', color: 'var(--t-rust)', cursor: 'pointer', padding: 0, fontSize: 11 }}>
                      Usuń
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Field>

          {photoFile && (
            <>
              <Field label="Autor zdjęcia *" htmlFor="apa">
                <input id="apa" type="text" value={photoAuthor}
                  onChange={e => setPhotoAuthor(e.target.value)}
                  placeholder="Twoje imię lub źródło (np. CC: Wikimedia/JaneDoe)"
                  maxLength={120} style={addPlantInputStyle} />
              </Field>
              <Field label="Licencja" htmlFor="apli">
                <input id="apli" type="text" value={photoLicense}
                  onChange={e => setPhotoLicense(e.target.value)}
                  placeholder="np. CC BY 4.0, własne, public domain"
                  maxLength={60} style={addPlantInputStyle} />
              </Field>
            </>
          )}

          {err && (
            <div style={{
              marginBottom: 12, padding: '10px 12px', borderRadius: 8,
              background: 'rgba(192, 106, 74, 0.10)',
              color: 'var(--t-rust)', fontSize: 12,
            }}>{err}</div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>Anuluj</button>
            <button type="submit" disabled={saving} style={primaryBtnStyle}>
              {saving ? 'Dodaję…' : 'Dodaj roślinę'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={htmlFor} style={{
        display: 'block', marginBottom: 6,
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-text-mute)',
      }}>{label}</label>
      {children}
    </div>
  );
}

const addPlantInputStyle = {
  width: '100%',
  padding: '10px 12px', borderRadius: 10,
  background: 'var(--t-bg)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtnStyle = {
  padding: '10px 18px', borderRadius: 10,
  background: 'var(--t-sage)', border: 'none',
  color: 'var(--t-bg)',
  fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
  cursor: 'pointer', letterSpacing: '-.01em',
};

const cancelBtnStyle = {
  padding: '10px 18px', borderRadius: 10,
  background: 'transparent',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text-dim)',
  fontFamily: 'var(--t-sans)', fontSize: 14,
  cursor: 'pointer',
};
